import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck } from 'lucide-react';
import { useViewerNotifications } from '../hooks/useViewerNotifications';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// Only allow same-origin paths (exactly one leading slash, no backslashes,
// no scheme). Anything else is treated as an invalid link.
function isSafeInternalPath(link) {
  return (
    typeof link === 'string' &&
    link.startsWith('/') &&
    !link.startsWith('//') &&
    !link.includes('\\') &&
    !/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(link)
  );
}

export default function Notifications() {
  const navigate = useNavigate();
  const { notifications, loading, markRead, markAllRead } = useViewerNotifications();

  const hasUnread = notifications.some((n) => !n.read_at);

  const handleOpen = async (n) => {
    if (!n.read_at) await markRead(n.id);
    if (n.link && isSafeInternalPath(n.link)) navigate(n.link);
  };

  return (
    <div className="max-w-2xl mx-auto px-6 py-10">
      <div className="flex items-center justify-between mb-7">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Notifications</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Activity on your Kinora account.</p>
        </div>
        {hasUnread && (
          <Button variant="outline" size="sm" onClick={markAllRead}>
            <CheckCheck className="h-4 w-4 mr-1.5" />
            Mark all read
          </Button>
        )}
      </div>

      {loading ? (
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-20 rounded-xl" />
          ))}
        </div>
      ) : notifications.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center gap-3">
          <Bell className="h-10 w-10 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">No notifications yet.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-border">
          {notifications.map((n) => {
            const unread = !n.read_at;
            return (
              <button
                key={n.id}
                onClick={() => handleOpen(n)}
                className={cn(
                  'flex items-start gap-3 w-full border-b border-border p-4 text-left transition-colors last:border-b-0 hover:bg-secondary/40',
                  unread && 'bg-secondary/20'
                )}
              >
                <div
                  className={cn(
                    'mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md',
                    unread ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'
                  )}
                >
                  <Bell className="h-4 w-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <p className={cn('text-sm', unread ? 'font-semibold' : 'font-medium')}>
                      {n.title}
                    </p>
                    <span className="shrink-0 text-xs text-muted-foreground">
                      {timeAgo(n.created_at)}
                    </span>
                  </div>
                  {n.body && (
                    <p className="mt-0.5 text-sm text-muted-foreground">{n.body}</p>
                  )}
                </div>
                {unread && <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
